import { useRestPaginatedQuery } from "../useRestPaginatedQuery";
import { querykeys } from "../constants";
import { useAuthUser } from "@/context/auth/AuthContext";
import { hasEmptyWarehouseScope } from "@/utils/warehouseScope";

/**
 * Locales del negocio.
 *
 * `hasPagination` viene en **false** por defecto a proposito. Los locales son
 * un set chico y acotado (son tiendas fisicas) y CASI TODOS los consumidores
 * son selectores que los pintan enteros: con el default paginado en 10, un
 * negocio con mas locales no veia los que caian afuera de la primera pagina —
 * el local no aparecia en el selector de la venta, en el alta de usuario ni en
 * los formularios de clinical, sin ningun aviso.
 *
 * Los dos consumidores que SI paginan de verdad (SelectWarehousesDialog y
 * WarehousesFilter, ambos con su `<Pagination>`) ya pasaban `true` explicito.
 */
function useGetWarehousesRest(
  operators: any = [],
  enabled: any = true,
  uniqueQuery: any = [],
  hasPagination: any = false,
  fetchInventories: any = false,
  limitWarehouses: boolean = false
) {
  const user = useAuthUser();
  const isNotOwner = user?.type !== "owner";
  const assignedWarehouses = user?.assignedWarehouses || [];

  /**
   * Un alcance VACÍO significa CERO locales, nunca TODOS: si el usuario no tiene
   * ni un local asignado, la query NI SE PIDE. El porqué está en
   * `hasEmptyWarehouseScope`, que es donde vive la regla (y donde está su test).
   *
   * `noWarehouseScope` viaja hasta la pantalla para que pueda decir POR QUÉ está
   * vacío, en vez de dejar un blanco que parece que la app se colgó.
   */
  const noWarehouseScope = hasEmptyWarehouseScope({
    userType: user?.type,
    assignedWarehouses: user?.assignedWarehouses,
    limitWarehouses,
  });

  const allOperators = [
    ...operators,
    ...(limitWarehouses && isNotOwner
      ? [
          {
            fieldName: "id",
            operation: "_in",
            variableName: "$warehousesId",
            variableValue: assignedWarehouses,
            type: "[String]",
            operator: "where",
          },
        ]
      : []),
  ];

  const extraParams: Record<string, any> = {};
  if (fetchInventories) {
    extraParams.include = "inventories";
  }

  const { query, items, ...pagination } = useRestPaginatedQuery({
    queryKey: querykeys.warehouses,
    url: "/api/v1/warehouses",
    operators: allOperators,
    enabled: enabled && !noWarehouseScope,
    uniqueQuery: [
      ...uniqueQuery,
      ...(limitWarehouses && isNotOwner ? ["limited-warehouses"] : []),
    ],
    hasPagination,
    extraParams,
  });

  return {
    warehouses: query,
    items,
    /** true = el usuario no tiene NINGÚN local asignado; la query no se pidió. */
    noWarehouseScope,
    ...pagination,
  };
}

export default useGetWarehousesRest;
